import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submitButton", "filterInput", "pendingIndicator", "helpText"]
  static values = { 
    formUrl: String 
  }

  connect() {
    this.hasPendingChanges = false
    this.originalFormData = new FormData(this.element)
    
    // Listen for multi-select changes (both controller types)
    this.element.addEventListener('multi-select:change', this.handleMultiSelectChange.bind(this))
    this.element.addEventListener('multiSelectChange', this.handleMultiSelectChange.bind(this))
    
    // Listen for other form changes
    this.element.addEventListener('change', this.handleFormChange.bind(this))
    this.element.addEventListener('input', this.handleFormChange.bind(this))
    
    this.updateButtonState()
  }

  disconnect() {
    this.element.removeEventListener('multi-select:change', this.handleMultiSelectChange.bind(this))
    this.element.removeEventListener('multiSelectChange', this.handleMultiSelectChange.bind(this))
    this.element.removeEventListener('change', this.handleFormChange.bind(this))
    this.element.removeEventListener('input', this.handleFormChange.bind(this))
  }

  handleMultiSelectChange(event) {
    // Mark as having pending changes when multi-select values change
    this.hasPendingChanges = true
    this.updateButtonState()
    this.showPendingIndicator()
  }

  handleFormChange(event) {
    // Skip if this is from a multi-select (already handled)
    if (event.target.closest('[data-controller*="multi-select"]')) {
      return
    }
    
    // Skip if this is search input (auto-submits)
    if (event.target.name === 'search') {
      return
    }
    
    // Skip if this is sort dropdown (auto-submits)
    if (event.target.name === 'sort') {
      return
    }
    
    // For other form changes, check if we have pending changes
    const currentFormData = new FormData(this.element)
    this.hasPendingChanges = !this.formDataEqual(this.originalFormData, currentFormData)
    this.updateButtonState()
    
    if (this.hasPendingChanges) {
      this.showPendingIndicator()
    } else {
      this.hidePendingIndicator()
    }
  }

  trackChange(event) {
    this.handleFormChange(event)
  }

  submit(event) {
    event.preventDefault()
    
    if (this.isSubmitting) return
    
    this.isSubmitting = true
    this.setLoadingState(true)
    
    try {
      // Submit the form
      this.element.submit()
    } catch (error) {
      console.error('Filter application error:', error)
      this.setLoadingState(false)
      this.showError('Failed to apply filters. Please try again.')
    }
  }

  clearAllFilters(event) {
    event.preventDefault()
    
    if (this.isSubmitting) return
    
    this.setLoadingState(true)
    
    // Navigate to the clean URL
    window.location.href = this.formUrlValue
  }

  updateButtonState() {
    if (this.hasSubmitButtonTarget) {
      const shouldEnable = this.hasPendingChanges && !this.isSubmitting
      
      if (shouldEnable) {
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
        this.submitButtonTarget.classList.add('hover:bg-blue-700', 'focus:ring-2')
      } else {
        this.submitButtonTarget.disabled = !this.hasPendingChanges
        if (!this.hasPendingChanges) {
          this.submitButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
          this.submitButtonTarget.classList.remove('hover:bg-blue-700', 'focus:ring-2')
        }
      }
    }
  }

  setLoadingState(isLoading) {
    this.isSubmitting = isLoading
    
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = isLoading
      
      if (isLoading) {
        this.submitButtonTarget.classList.add('opacity-75', 'cursor-not-allowed')
        this.submitButtonTarget.classList.remove('hover:bg-blue-700')
      } else {
        this.submitButtonTarget.classList.remove('opacity-75', 'cursor-not-allowed')
        if (this.hasPendingChanges) {
          this.submitButtonTarget.classList.add('hover:bg-blue-700')
        }
      }
    }
    
    // Update button text if it has a text element
    const buttonText = this.submitButtonTarget?.querySelector('span, .button-text')
    if (buttonText) {
      if (isLoading) {
        buttonText.textContent = 'Applying...'
      } else {
        buttonText.textContent = 'Apply Filters'
      }
    }
    
    this.updateButtonState()
  }

  showPendingIndicator() {
    if (this.hasPendingIndicatorTarget) {
      this.pendingIndicatorTarget.classList.remove('hidden')
    }
    
    if (this.hasHelpTextTarget) {
      this.helpTextTarget.innerHTML = `
        <div class="flex items-start space-x-2">
          <svg class="w-4 h-4 mt-0.5 text-orange-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          <div>
            <p class="font-medium text-orange-800">Pending filter changes</p>
            <p class="text-xs mt-1 text-orange-700">You have unsaved filter selections. Click "Apply Filters" to update results.</p>
          </div>
        </div>
      `
    }
  }

  hidePendingIndicator() {
    if (this.hasPendingIndicatorTarget) {
      this.pendingIndicatorTarget.classList.add('hidden')
    }
    
    if (this.hasHelpTextTarget) {
      this.helpTextTarget.innerHTML = `
        <div class="flex items-start space-x-2">
          <svg class="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
          <div>
            <p class="font-medium">How filtering works:</p>
            <p class="text-xs mt-1">Select multiple options within each category. Click "Apply Filters" to combine your selections. Search updates automatically as you type.</p>
          </div>
        </div>
      `
    }
  }

  formDataEqual(formData1, formData2) {
    const entries1 = Array.from(formData1.entries()).sort()
    const entries2 = Array.from(formData2.entries()).sort()
    
    if (entries1.length !== entries2.length) return false
    
    for (let i = 0; i < entries1.length; i++) {
      if (entries1[i][0] !== entries2[i][0] || entries1[i][1] !== entries2[i][1]) {
        return false
      }
    }
    
    return true
  }

  showError(message) {
    // Create or update error notification
    let errorDiv = document.getElementById('manual-filter-error')
    if (!errorDiv) {
      errorDiv = document.createElement('div')
      errorDiv.id = 'manual-filter-error'
      errorDiv.className = 'fixed top-4 right-4 max-w-sm bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg shadow-lg z-50'
      document.body.appendChild(errorDiv)
    }

    errorDiv.innerHTML = `
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium">${message}</p>
        </div>
        <div class="ml-auto pl-3">
          <button type="button" class="inline-flex text-red-400 hover:text-red-600 focus:outline-none" onclick="this.parentElement.parentElement.parentElement.remove()">
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    `

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (errorDiv && errorDiv.parentNode) {
        errorDiv.remove()
      }
    }, 5000)
  }
}